import { Honeybadger } from '@honeybadger-io/react';
import { Button, ButtonVariants } from '@triumph/design-system';
import { Message } from 'primereact/message';
import { ComponentType, ErrorInfo, ReactElement, ReactNode } from 'react';
import { FallbackProps, ErrorBoundary as ReactErrorBoundary } from 'react-error-boundary';

import './ErrorBoundary.css';

import { EVENT_LOG_NAMES } from '@/components/shared/utils/logging';
import { SupportContactMessage } from '../SupportContactMessage';

const FALLBACK_MESSAGE = (
    <SupportContactMessage
        lead="Something went wrong."
        copyLinkTestId="banner-copy-orp-link-button"
    />
);

export const LayoutErrorFallback = ({ resetErrorBoundary }: FallbackProps) => (
    <div data-testid="error-boundary-fallback" style={{ padding: '2rem' }}>
        <Message severity="error" text={FALLBACK_MESSAGE} />
        <div style={{ marginTop: '1rem' }}>
            <Button variant={ButtonVariants.primary} onClick={() => resetErrorBoundary()}>
                Reload page
            </Button>
        </div>
    </div>
);

export const RootErrorBoundaryFallback = () => (
    <div
        data-testid="root-error-boundary-fallback"
        style={{
            padding: '2rem',
            textAlign: 'center',
            fontFamily: 'sans-serif',
        }}
    >
        <h1>Something went wrong.</h1>
        <p>The page failed to load. Please reload to try again or contact support.</p>
        <button type="button" onClick={() => window.location.reload()}>
            Reload
        </button>
    </div>
);

type ErrorBoundaryLocation = 'root' | 'layout';

interface ErrorBoundaryProps {
    children: ReactNode;
    location: ErrorBoundaryLocation;
    fallback?: ReactElement;
    FallbackComponent?: ComponentType<FallbackProps>;
    onReset?: () => void;
}

const buildErrorReporter =
    (location: ErrorBoundaryLocation) =>
    (error: unknown, info: ErrorInfo): void => {
        const normalizedError = error instanceof Error ? error : new Error(String(error));
        const componentStack = info.componentStack ?? '';

        Honeybadger.notify(normalizedError, {
            name: EVENT_LOG_NAMES.ErrorBoundaryCaught,
            context: { componentStack, location },
        });
    };

export const ErrorBoundary = ({
    children,
    location,
    fallback,
    FallbackComponent,
    onReset,
}: ErrorBoundaryProps) => {
    const onError = buildErrorReporter(location);

    if (fallback) {
        return (
            <ReactErrorBoundary fallback={fallback} onError={onError} onReset={onReset}>
                {children}
            </ReactErrorBoundary>
        );
    }

    return (
        <ReactErrorBoundary
            FallbackComponent={FallbackComponent ?? LayoutErrorFallback}
            onError={onError}
            onReset={onReset}
        >
            {children}
        </ReactErrorBoundary>
    );
};
